import { useState, useEffect, useCallback } from 'react';
import type { Product } from '@/lib/types';
import {
  getProducts,
  saveProduct,
  deleteProduct,
} from '@/lib/api';
import ImageUploader from '@/pages/admin/components/ImageUploader';
import { useProductContext } from '@/context/ProductContext';

const CATEGORIES = [
  'Pasteles Grandes',
  'Pasteles Medianos',
  'Pasteles Individuales',
  'Galletas',
  'Gelatinas',
  'Panqués y Roscas',
  'Tartas',
  'Sin Azúcar',
  'Productos para perrito',
  'Helados',
];

const BADGES = ['', 'FAVORITO', 'NUEVO', 'PERSONALIZABLE', 'CLÁSICO', 'TEMPORADA'];

const emptyForm: Omit<Product, 'id' | 'created_at' | 'updated_at'> = {
  name: '',
  category: 'Pasteles Individuales',
  price: 0,
  description: '',
  image: '',
  badge: '',
  priority: 1,
  active: true,
  featured_month: false,
};

export default function AdminProductos() {
  const { refreshProducts } = useProductContext();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('Todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<Omit<Product, 'id' | 'created_at' | 'updated_at'>>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      console.error(error);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filtered = products.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'Todos' || p.category === filterCat;
    return matchSearch && matchCat;
  });

  const openCreate = () => {
    setEditingProduct(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      category: product.category,
      price: product.price,
      description: product.description,
      image: product.image,
      badge: product.badge,
      priority: product.priority,
      active: product.active,
      featured_month: product.featured_month,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) return;

    setSaving(true);

    try {
      if (editingProduct) {
        await saveProduct({
          ...form,
          id: editingProduct.id,
        });

        showToast("Producto actualizado correctamente");
      } else {
        await saveProduct(form);

        showToast("Producto creado correctamente");
      }

      setModalOpen(false);

      await fetchProducts();

      await refreshProducts();
    } catch (error) {
      console.error(error);
      showToast("Error al guardar el producto");
    }

    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteProduct(id);

      showToast("Producto eliminado");

      await fetchProducts();

      await refreshProducts();
    } catch (error) {
      console.error(error);
    }

    setDeleteConfirm(null);
  };

  const toggleActive = async (product: Product) => {
    try {
      await saveProduct({
        ...product,
        active: !product.active,
      });

      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id
            ? { ...p, active: !p.active }
            : p
        )
      );

      await refreshProducts();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#2d1a0e] text-white text-sm px-5 py-3 rounded-xl animate-fade-in">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-emerald-400"></i>
            {toast}
          </span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#2d1a0e]">
            Gestión de Productos
          </h1>
          <p className="text-sm text-[#8a6a55]">{products.length} productos en total</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#00b4c8] hover:bg-[#0099aa] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-sm"></i>
          </span>
          Nuevo Producto
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#8a6a55]">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar producto..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[#c9b8f0]/40 bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] cursor-pointer"
        >
          <option value="Todos">Todas las categorías</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-[#f0e6d8] overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[#8a6a55] text-sm">
            <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-loader-4-line text-2xl animate-spin text-[#00b4c8]"></i>
            </span>
            Cargando productos...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f8f4ff] border-b border-[#c9b8f0]/20">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider">Producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider hidden md:table-cell">Categoría</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider">Precio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider hidden sm:table-cell">Prioridad</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider">Estado</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f0e6d8]">
                {filtered.map((product) => (
                  <tr key={product.id} className="hover:bg-[#fdf6f0] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-[#2d1a0e] truncate max-w-[160px]">{product.name}</p>
                          {product.badge && (
                            <span className="text-xs bg-[#d4607a]/10 text-[#d4607a] px-1.5 py-0.5 rounded-full">
                              {product.badge}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#5a3e2b] hidden md:table-cell">{product.category}</td>
                    <td className="px-4 py-3 font-semibold text-[#d4607a]">${product.price}</td>
                    <td className="px-4 py-3 text-[#5a3e2b] hidden sm:table-cell">{product.priority}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(product)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium cursor-pointer transition-colors whitespace-nowrap ${
                          product.active
                            ? 'bg-[#00b4c8]/10 text-[#00b4c8] hover:bg-[#00b4c8]/20'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {product.active ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(product)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] text-[#6b5b6e] hover:text-[#00b4c8] transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(product.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#5a3e2b] hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-[#8a6a55] text-sm">
                      No se encontraron productos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-[#c9b8f0]/20 flex items-center justify-between">
              <h3 className="font-bold text-[#2d1a0e]">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f8f4ff] cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-[#6b5b6e] text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Nombre del Producto *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                  placeholder="Ej: Pastel Red Velvet Individual"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Categoría *
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] cursor-pointer"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Precio (MXN) *
                  </label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                    placeholder="0"
                    min={0}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Descripción
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors resize-none"
                  placeholder="Descripción del producto..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Badge / Etiqueta
                  </label>
                  <select
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] cursor-pointer"
                  >
                    {BADGES.map((b) => (
                      <option key={b} value={b}>{b || 'Sin etiqueta'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                    Prioridad
                  </label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
                    min={1}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#7c5cbf] uppercase tracking-wider mb-1.5">
                  Imagen del Producto
                </label>
                <ImageUploader
                  value={form.image}
                  onChange={(base64) => setForm({ ...form, image: base64 })}
                  accentColor="#00b4c8"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, active: !form.active })}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.active ? 'bg-[#00b4c8]' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.active ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-sm text-[#5a3e2b]">Producto activo</span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#c9b8f0]/20 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-[#c9b8f0]/40 text-sm text-[#6b5b6e] hover:bg-[#f8f4ff] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || !form.price || saving}
                className="px-4 py-2 rounded-lg bg-[#00b4c8] hover:bg-[#0099aa] text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-center font-bold text-[#2d1a0e] mb-2">¿Eliminar producto?</h3>
            <p className="text-center text-sm text-[#8a6a55] mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-[#e8d5c4] text-sm text-[#5a3e2b] hover:bg-[#fdf6f0] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
